import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Trash2, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { ProvinciaSelect } from "@/components/ProvinciaSelect";

const ROLE_LABEL = { admin: "Amministratore", coordinatore: "Coordinatore", territoriale: "Territoriale", com_distaccamento: "Com. Distaccamento", guardia: "Guardia" };
const ROLE_CLS = {
  admin: "border-[#D97706] bg-amber-50 text-[#D97706]",
  coordinatore: "border-[#14382C] bg-emerald-50 text-[#14382C]",
  territoriale: "border-blue-200 bg-blue-50 text-blue-700",
  com_distaccamento: "border-indigo-200 bg-indigo-50 text-indigo-700",
  guardia: "border-slate-200 bg-slate-50 text-slate-600",
};

export default function Utenti() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "guardia", guardia_id: "" });

  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: guardie = [] } = useQuery({ queryKey: ["guardie-all"], queryFn: async () => (await api.get("/guardie")).data });

  const pending = users.filter((u) => u.approved === false);

  const submit = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Compila nome, email e password"); return; }
    setSaving(true);
    try {
      await api.post("/users", { ...form, guardia_id: form.guardia_id || null });
      toast.success("Account creato");
      qc.invalidateQueries({ queryKey: ["users"] });
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "guardia", guardia_id: "" });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const approve = async (id) => {
    try { await api.post(`/users/${id}/approve`); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Utente approvato"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Utente eliminato"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const changeRole = async (id, role) => {
    try { await api.patch(`/users/${id}/role`, { role }); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Livello di autorizzazione aggiornato"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const setProvincia = async (id, provincia) => {
    try { await api.patch(`/users/${id}/provincia`, { provincia: provincia || null }); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Provincia aggiornata"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const linkGuardia = async (id, guardia_id) => {
    try { await api.patch(`/users/${id}/link`, { guardia_id }); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Collegamento alla scheda aggiornato"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="gz-fade-up">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Utenti</h1>
          <p className="mt-1 text-sm text-slate-500">Account, ruoli e approvazione dei primi accessi via Google.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#14382C] hover:bg-[#0E281F]" data-testid="new-user-button"><Plus className="mr-2 h-4 w-4" /> Nuovo utente</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Crea account</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Nome</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Password</Label><Input type="text" data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="guardia">Guardia</SelectItem>
                    <SelectItem value="territoriale">Territoriale</SelectItem>
                    <SelectItem value="com_distaccamento">Com. Distaccamento</SelectItem>
                    <SelectItem value="coordinatore">Coordinatore</SelectItem>
                    <SelectItem value="admin">Amministratore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "guardia" && (
                <div className="space-y-2">
                  <Label>Abbina a scheda guardia (CF)</Label>
                  <Select value={form.guardia_id} onValueChange={(v) => setForm({ ...form, guardia_id: v })}>
                    <SelectTrigger data-testid="user-guardia"><SelectValue placeholder="Seleziona guardia" /></SelectTrigger>
                    <SelectContent>{guardie.map((g) => <SelectItem key={g.id} value={g.id}>{g.cognome} {g.nome} — {g.codice_fiscale}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={submit} disabled={saving} data-testid="save-user-button" className="bg-[#14382C] hover:bg-[#0E281F]">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Crea account</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {pending.length > 0 && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="pending-approvals">
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-900"><Clock className="h-4 w-4" /> {pending.length} accesso/i in attesa di approvazione</p>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white p-3" data-testid={`pending-${u.id}`}>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{u.name || u.email}</p>
                  <p className="text-xs text-slate-500">{u.email} · {u.auth_provider === "google" ? "Accesso Google" : "Password"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" className="bg-[#166534] hover:bg-[#14532d]" data-testid={`approve-${u.id}`} onClick={() => approve(u.id)}><Check className="mr-1.5 h-4 w-4" /> Approva</Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" data-testid={`reject-${u.id}`} onClick={() => remove(u.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Ruolo</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3">Scheda collegata</th><th className="px-4 py-3">Provincia (comandanti)</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-semibold text-slate-900">{u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  {u.id === user.id ? (
                    <Badge variant="outline" className={ROLE_CLS[u.role] || ROLE_CLS.guardia}>{ROLE_LABEL[u.role] || u.role}</Badge>
                  ) : (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger className="h-8 w-[160px]" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="guardia">Guardia</SelectItem>
                        <SelectItem value="territoriale">Territoriale</SelectItem>
                    <SelectItem value="com_distaccamento">Com. Distaccamento</SelectItem>
                        <SelectItem value="coordinatore">Coordinatore</SelectItem>
                        <SelectItem value="admin">Amministratore</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.approved === false
                    ? <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">In attesa</Badge>
                    : <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">Attivo</Badge>}
                </td>
                <td className="px-4 py-3">
                  <Select value={u.guardia_id || "none"} onValueChange={(v) => linkGuardia(u.id, v === "none" ? null : v)}>
                    <SelectTrigger className="h-8 w-[220px]" data-testid={`link-select-${u.id}`}><SelectValue placeholder="Collega scheda" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuna scheda</SelectItem>
                      {guardie.map((g) => <SelectItem key={g.id} value={g.id}>{g.cognome} {g.nome} — {g.codice_fiscale}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  {["territoriale", "com_distaccamento"].includes(u.role) ? (
                    <ProvinciaSelect className="h-8 w-[200px]" value={u.provincia} onChange={(v) => setProvincia(u.id, v)} testid={`user-provincia-${u.id}`} emptyLabel="Dalla scheda anagrafica" />
                  ) : <span className="text-xs text-slate-400">{u.role === "guardia" ? "—" : "Vede tutto"}</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {u.approved === false && <Button size="sm" className="h-7 bg-[#166534] px-2 hover:bg-[#14532d]" data-testid={`approve-row-${u.id}`} onClick={() => approve(u.id)}><Check className="h-4 w-4" /></Button>}
                    {u.id !== user.id && <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
